/**
 * Auth Layout Component
 * Updated: 2025-01-27 - Added dynamic theming based on app type (console vs trade)
 * - Detects app type from redirect_uri query parameter using centralized hook
 * - Applies console dark theme (#2b2e34) for console app logins
 * - Applies trade blue theme (#424AF9) for trade app logins
 * - Wraps content with ChakraProviderWithTheme to override primary color
 */

'use client'

import {Box, Card, CardBody, HStack, Image, Text} from '@chakra-ui/react'
import React from 'react'
import Link from 'next/link'
import {useSearchParams} from 'next/navigation'
import {ChakraProviderWithTheme} from '@frnt/base/providers'
import {useAppDetection} from '../../hooks/use-app-detection.hook'

interface AuthLayoutProps {
  children: React.ReactNode
}

export default function AuthLayout({children}: AuthLayoutProps) {
  const searchParams = useSearchParams()
  const loginHref = `/login?${searchParams.toString()}`
  const redirect_uri = searchParams.get('redirect_uri')

  // Detect if this is a console app login using centralized hook
  const {isConsoleLogin} = useAppDetection(redirect_uri)

  // Set primary color based on app type (console uses dark, trade uses default blue)
  const primaryColor = isConsoleLogin ? '#2b2e34' : undefined

  return (
    <ChakraProviderWithTheme primaryColor={primaryColor}>
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="primary.500"
        backgroundImage="url('/background-login.png')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center">
        <Box textAlign="center">
          <Link href={loginHref}>
            <Image src="/frnt-logo-white.png" alt="FRNT Logo" height="40px" mx="auto" />
          </Link>
        </Box>

        <HStack flex={1} maxH="calc(100svh - 100px)" py={2} px={1} justifyContent="center" alignItems="center">
          <Card w="480px" m="auto" maxW="100svw" maxH="full" bg="white" boxShadow="sm" borderRadius={['none', 'xl']} overflowY="auto">
            <CardBody px={6} py={6}>
              {children}
            </CardBody>
          </Card>
        </HStack>

        <Box>
          <Text fontSize="xs" color="gray.400">
            © 2025 FRNT Financial Inc. •{' '}
            <Text as="span" color="gray.400" textDecoration="underline">
              Privacy & terms
            </Text>
          </Text>
        </Box>
      </Box>
    </ChakraProviderWithTheme>
  )
}
